import React, { useState, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./passenger-card.scss";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Select, Input, ConfigProvider } from "antd";
import {
  phoneCountryCodes,
  iso2ToDialCode,
  getPhoneCodeByIso2,
  countryDialLabel,
} from "../../../constants/phoneCountryCodes";

export default function ContactInfoCard({ contactInfo, onSubmit, editable }) {
  const [isEditing, setIsEditing] = useState(editable);

  const extractDialCode = (c) => {
    // Prefer your canonical shape first
    if (c?.code && /^\+\d{1,4}$/.test(c.code)) return c.code;

    // Common direct keys
    const direct =
      c?.dial_code ||
      c?.dialCode ||
      c?.phone_code ||
      c?.phoneCode ||
      c?.calling_code ||
      c?.callingCode ||
      c?.country_calling_code ||
      c?.idd_code ||
      c?.idd ||
      null;

    if (typeof direct === "string" && /^\+\d{1,4}$/.test(direct)) return direct;

    // Arrays like callingCodes: ["+65"]
    const arr =
      c?.callingCodes || c?.calling_codes || c?.idds || c?.codes || c?.prefixes;
    if (Array.isArray(arr)) {
      const found = arr.find(
        (x) => typeof x === "string" && /^\+\d{1,4}$/.test(x)
      );
      if (found) return found;
    }

    // Objects like { idd: { root: "+6", suffixes: ["5","6"] } }
    if (
      c?.idd &&
      c.idd.root &&
      Array.isArray(c.idd.suffixes) &&
      c.idd.suffixes[0]
    ) {
      const combo = `${c.idd.root}${c.idd.suffixes[0]}`;
      if (/^\+\d{1,4}$/.test(combo)) return combo;
    }

    // Last resort: scan all string fields for a +NNN pattern
    for (const k of Object.keys(c)) {
      const v = c[k];
      if (typeof v === "string") {
        const m = v.match(/\+\d{1,4}/);
        if (m) return m[0];
      }
    }

    return null;
  };

  const extractISO2 = (c) =>
    (c?.iso2 || c?.iso2_cc || c?.alpha2 || c?.code2 || c?.cca2 || c?.code || "")
      .toString()
      .toUpperCase();

  /* Emoji from ISO2 (simple fallback) */
  const countryCodeToEmoji = (iso2) => {
    if (!iso2 || iso2.length !== 2) return "🏳️";
    const codePoints = [...iso2.toUpperCase()].map(
      (c) => 127397 + c.charCodeAt(0)
    );
    return String.fromCodePoint(...codePoints);
  };

  const phoneCountryOptions = useMemo(() => {
    if (!Array.isArray(phoneCountryCodes)) return [];

    const opts = phoneCountryCodes
      .map((c) => {
        const iso2 = extractISO2(c);
        const dial = extractDialCode(c) || iso2ToDialCode?.[iso2] || null;
        if (!iso2 || !dial) return null;
        return {
          iso2,
          name: c.name || iso2,
          dial,
          label: `${countryCodeToEmoji(iso2)} ${iso2} ${dial}`,
        };
      })
      .filter(Boolean);

    // Put SG first for UX, then alpha by name
    opts.sort((a, b) => {
      if (a.iso2 === "SG") return -1;
      if (b.iso2 === "SG") return 1;
      return a.name.localeCompare(b.name);
    });

    return opts;
  }, []);

  const contantInfoFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: contactInfo?.id || "",
      name: contactInfo?.name || "",
      email: contactInfo?.email || "",
      phoneAreaCode: contactInfo?.phoneAreaCode || "+65",
      phoneNumber: contactInfo?.phoneNumber || "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .matches(/^[A-Za-z\s]+$/, "Only English letters allowed")
        .required("Name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Last name is required"),
      phoneAreaCode: Yup.string().required("phoneAreaCode is required"),
      phoneNumber: Yup.string().required("phoneNumber is required"),
    }),
    onSubmit: (values) => {
      onSubmit?.(values);
      setIsEditing(false);
    },
  });

  if (!isEditing && contactInfo) {
    // View-card
    return (
      <div className="passenger-card view-card">
        <div className="passenger-card-left"></div>
        <div className="passenger-card-right">
          <div className="card-header">
            <div className="passenger-name">
              {contantInfoFormik.values.name}
            </div>
            <div>
              <button
                className="edit-btn"
                style={{ marginRight: "12px" }}
                onClick={() => setIsEditing(true)}
              >
                <EditOutlined />
              </button>
              <button className="edit-btn" onClick={() => setIsEditing(true)}>
                <DeleteOutlined />
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="passenger-info-row">
              <span className="info-label">{`${contantInfoFormik.values.phoneAreaCode} ${contantInfoFormik.values.phoneNumber}/${contantInfoFormik.values.email}`}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      className="passenger-card edit-card"
      onSubmit={contantInfoFormik.handleSubmit}
    >
      <div className="card-header">
        <h5>Edit Contact Info</h5>
      </div>

      <div className="card-body row">
        <div className="guest-inputs">
          <div className="col-md-12 p-2">
            <div className="floating-label">
              <input
                type="text"
                name="name"
                placeholder="Contact Name"
                value={contantInfoFormik.values.name}
                onChange={contantInfoFormik.handleChange}
                onBlur={contantInfoFormik.handleBlur}
              />
              <label className="floating-label-text">Contact Name</label>
              {contantInfoFormik.touched.firstName &&
                contantInfoFormik.errors.firstName && (
                  <div className="error">
                    {contantInfoFormik.errors.firstName}
                  </div>
                )}
            </div>
          </div>

          <div className="col-md-12 p-2">
            <div className="floating-label">
              <div className="label-text">Phone number</div>
            </div>
            <div className="mp-phone-row">
              <ConfigProvider
                theme={{
                  components: {
                    Select: {
                      activeBorderColor: "#CBA135",
                      hoverBorderColor: "#CBA135",
                    },
                  },
                  token: {
                    controlHeight: 36,
                  },
                }}
              >
                <Select
                  value={contantInfoFormik.values.phoneAreaCode}
                  onChange={(iso2) => {
                    const chosen = phoneCountryOptions.find(
                      (o) => o.iso2 === iso2
                    );
                    if (chosen)
                      contantInfoFormik.setFieldValue(
                        "phoneAreaCode",
                        chosen.dial
                      );
                  }}
                  className="mp-phone-code-select"
                  showSearch
                  optionFilterProp="label"
                  options={phoneCountryOptions.map((o) => ({
                    value: o.iso2,
                    label: o.label,
                    dial: o.dial,
                  }))}
                  dropdownRender={(menu) => (
                    <div style={{ padding: 4 }}>{menu}</div>
                  )}
                />
              </ConfigProvider>
              <ConfigProvider
                theme={{
                  components: {
                    Input: {
                      activeBorderColor: "#CBA135",
                      hoverBorderColor: "#CBA135",
                    },
                  },
                }}
              >
                <Input
                  name="phoneNumber"
                  className="mp-phone-input"
                  value={contantInfoFormik.values.phoneNumber}
                  onChange={contantInfoFormik.handleChange}
                  placeholder="Enter phone number"
                  inputMode="numeric"
                />
              </ConfigProvider>
            </div>
            {contantInfoFormik.touched.phoneNumber &&
              contantInfoFormik.errors.phoneNumber && (
                <div className="error">
                  {contantInfoFormik.errors.phoneNumber}
                </div>
              )}
          </div>

          {/* Nationality */}
          <div className="col-md-12 p-2">
            <div className="floating-label">
              <input
                type="text"
                name="email"
                placeholder="Email"
                value={contantInfoFormik.values.email}
                onChange={contantInfoFormik.handleChange}
                onBlur={contantInfoFormik.handleBlur}
              />
              <label className="floating-label-text">Email</label>
              {contantInfoFormik.touched.email &&
                contantInfoFormik.errors.email && (
                  <div className="error">{contantInfoFormik.errors.email}</div>
                )}
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="d-flex">
          <div
            className="col-12 p-2 d-flex gap-2"
            style={{ justifyContent: "flex-end" }}
          >
            <Button
              className="close-btn"
              disabled={!contactInfo}
              onClick={() => {
                setIsEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              className="save-btn"
              loading={contantInfoFormik.isSubmitting}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
